import { Fragment } from '@atlaskit/editor-prosemirror/model';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import { replaceConfluenceTitle } from '../../../ui/prebuilt/config-items/actions/replace-title';
import { CONFIG_ITEM_KEYS } from '../../../ui/prebuilt/config-items/config-item-keys';
import { aiSessionContextPluginKey } from '../../ai-session-context/ai-session-context-plugin-key';
import { aiExperienceDecorationPluginKey } from '../../decoration/decoration-plugin-key';
import type { PurePmInEditorDirectStreamingPluginState } from '../types';

/**
 * Mutable state container for suggest title event publishing.
 * Tracks which response version has been published to avoid duplicate events.
 */
export interface SuggestTitleViewState {
	lastPublishedVersion: number | null;
}

/**
 * Creates the initial suggest title view state.
 */
export function createSuggestTitleViewState(): SuggestTitleViewState {
	return {
		lastPublishedVersion: null,
	};
}

/**
 * Handles publishing the 'replace title in confluence' event during suggest title streaming.
 *
 * The suggest title streaming response provides markdown text (not ADF).
 * This view-update handler converts the markdown to a ProseMirror fragment
 * and calls the same `replaceConfluenceTitle` function used by the legacy flow,
 * which publishes the eventHub event to update the Confluence page title textarea.
 */
export function handleSuggestTitle(
	view: EditorView,
	state: PurePmInEditorDirectStreamingPluginState,
	suggestTitleState: SuggestTitleViewState,
): void {
	// Only process when actively streaming or just finished streaming
	if (!state.isStreaming && !state.isStreamingFinished) {
		return;
	}

	const decorationState = aiExperienceDecorationPluginKey.getState(view.state);
	if (!decorationState?.configItem) {
		return;
	}

	// Only handle suggest title config
	if (decorationState.configItem.key !== CONFIG_ITEM_KEYS.SUGGEST_A_TITLE) {
		return;
	}

	const sessionState = aiSessionContextPluginKey.getState(view.state);
	if (!sessionState?.markdown) {
		return;
	}

	// Skip if we already published this version
	if (
		suggestTitleState.lastPublishedVersion !== null &&
		sessionState.responseVersion === suggestTitleState.lastPublishedVersion
	) {
		return;
	}

	// Create a ProseMirror fragment from the markdown title text
	const textNode = view.state.schema.text(sessionState.markdown);
	const fragment = Fragment.from(textNode);

	const isFinal = sessionState.status === 'complete';

	// Use the same replaceConfluenceTitle function as the legacy flow.
	// This publishes the 'replace title in confluence' eventHub event,
	// which useEditorAIReplacingTitle subscribes to for updating the Confluence page title.
	replaceConfluenceTitle({
		editorView: view,
		pmFragment: fragment,
		isFinal,
	});

	// Track published version to avoid duplicates
	suggestTitleState.lastPublishedVersion = sessionState.responseVersion ?? null;
}
